import NavTab from "features/shared/components/navs/NavTab";
import TabsNavigation from "features/shared/components/navs/TabsNavigation";
import { getContractDetailRootRoute, getContractDetailRoute } from "features/shared/helpers/routeHelper";
import React from "react";
import { useTranslation } from "react-i18next";
import ContractEnergyDTO from "../dtos/ContractEnergyDTO";
import { ContractCategory } from "../enums/contractCategory";
import { ContractStatus } from "../enums/contractStatus";
import { ContractType } from "../enums/contractType";
import { isEnabledCustomForm } from "features/requirements/helpers/requirementTypeHelper";

const selfReadingAllowedStatuses = [ContractStatus.Valid, ContractStatus.Effective, ContractStatus.Terminated];
const reservedCapacityAllowTypes: ContractType[] = ["ElectricityHighVoltage", "GasLargeConsumption"];
const reservedCapacityAllowMeterTypes: string[] = ["A", "B"];
const reservedCapacityAllowCategories: string[] = [ContractCategory.CombinedServices, ContractCategory.Distribution];

interface Props {
  contractId: string;
  contractEnergy: ContractEnergyDTO;
  contractType: ContractType;
  contractStatus: ContractStatus;
  contractCategory: ContractCategory;
  isProduction: boolean;
}

const ContractPagesNavigation = ({ contractId, contractEnergy, contractType, contractStatus, contractCategory, isProduction }: Props) => {
  // translations
  const tLabels = useTranslation("contracts\\contracts").t;

  // helepers
  const getHistoryTabTitle = (isProduction: boolean, onMobile: boolean): string => {
    let part1 = isProduction ? "productionHistory" : "consumptionHistory";
    let part2 = onMobile ? "mobile" : "desktop";
    
    return tLabels(`contractPagesNavigation.${part1}.${part2}`);
  }

  // sub-renders
  const renderSelfReadingLink = () => {
    if (
      isEnabledCustomForm("ZADANISAMOODECTU") &&
      (contractEnergy.meterType === "C" || contractEnergy.meterType === "CM") &&
      selfReadingAllowedStatuses.includes(contractStatus) &&
      !isProduction
    ) {
      return (
        <NavTab to={getContractDetailRootRoute(contractId, "self-readings")}>
          <span className="d-sm-none">
            {tLabels("contractPagesNavigation.selfReadings.mobile")}
          </span>
          <span className="d-none d-sm-inline">
            {tLabels("contractPagesNavigation.selfReadings.desktop")}
          </span>
        </NavTab>
      );
    }
  };

  const renderReservedCapacityLink = () => {
    if (
      reservedCapacityAllowTypes.includes(contractType) &&
      reservedCapacityAllowMeterTypes.includes(contractEnergy.meterType!) &&
      reservedCapacityAllowCategories.includes(contractCategory) &&
      !isProduction
    ) {
      return (
        <NavTab
          to={getContractDetailRootRoute(contractId, "reserved-capacity")}
        >
          <span className="d-sm-none">
            {tLabels("contractPagesNavigation.reservedCapacity.mobile")}
          </span>
          <span className="d-none d-sm-inline">
            {tLabels("contractPagesNavigation.reservedCapacity.desktop")}
          </span>
        </NavTab>
      );
    }
  };

  // render
  return (
    <TabsNavigation asModuleTabs>
      <NavTab
        exact
        to={getContractDetailRoute(contractId)}
      >
        <span className="d-sm-none"> {tLabels("contractPagesNavigation.contractDetail.mobile")}</span> 
        <span className="d-none d-sm-inline"> {tLabels("contractPagesNavigation.contractDetail.desktop")}</span> 
      </NavTab>
      {renderSelfReadingLink()}
      {renderReservedCapacityLink()}
      <NavTab
        to={getContractDetailRootRoute(contractId, "consumption-history")}
      >
        <span className="d-sm-none">{getHistoryTabTitle(isProduction, true)}</span> 
        <span className="d-none d-sm-inline">{getHistoryTabTitle(isProduction, false)}</span> 
      </NavTab>
      <NavTab
        to={getContractDetailRootRoute(contractId, "documents")}
      >
        {tLabels("contractPagesNavigation.documents")}
      </NavTab>
      </TabsNavigation>
  );
};

export default ContractPagesNavigation;
